/* global axios */
import ApiClient from './ApiClient';
import { requestConfig, resourcePath } from './lais/httpHelpers';
import { redactLaisPayload } from './lais/redaction';

const CRM_OPERATIONAL_KEYS = [
  'crm_sync',
  'crmSync',
  'crm_sync_attempts',
  'crmSyncAttempts',
  'sync_attempts',
  'syncAttempts',
  'lais_crm',
  'laisCrm',
  'raw_payload',
  'rawPayload',
  'request_payload',
  'requestPayload',
  'response_payload',
  'responsePayload',
  'payload',
  'metadata',
  'last_error',
  'lastError',
];

const isObject = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const firstPresent = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

export const contactsPayloadList = data => {
  if (Array.isArray(data?.payload)) return data.payload;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;
  return [];
};

export const contactsResponseCount = data => {
  const meta = data?.meta || {};
  const count = firstPresent(
    meta.count,
    meta.total_count,
    meta.totalCount,
    meta.total,
    contactsPayloadList(data).length
  );
  const number = Number(count);
  return Number.isFinite(number) ? number : 0;
};

export const redactCrmOperationalPayload = redactLaisPayload;

const normalizeOperationalAttributes = attributes => {
  if (!isObject(attributes)) return attributes || {};

  const normalized = { ...attributes };
  CRM_OPERATIONAL_KEYS.forEach(key => {
    if (normalized[key] !== undefined) {
      normalized[key] = redactCrmOperationalPayload(normalized[key]);
    }
  });

  return normalized;
};

export const normalizeLaisContact = contact => {
  if (!isObject(contact)) return {};

  const normalized = { ...contact };

  if (isObject(normalized.additional_attributes)) {
    normalized.additional_attributes = normalizeOperationalAttributes(
      normalized.additional_attributes
    );
  }

  CRM_OPERATIONAL_KEYS.forEach(key => {
    if (normalized[key] !== undefined) {
      normalized[key] = redactCrmOperationalPayload(normalized[key]);
    }
  });

  return normalized;
};

export const normalizeContactsListPayload = data => {
  if (Array.isArray(data)) {
    return data.map(normalizeLaisContact);
  }

  if (!isObject(data)) return { payload: [], meta: {} };

  const normalized = { ...data };
  const list = contactsPayloadList(data).map(normalizeLaisContact);

  if (Array.isArray(data.payload)) {
    normalized.payload = list;
  }
  if (Array.isArray(data.data)) {
    normalized.data = list;
  }

  return normalized;
};

export const normalizeContactPayload = data => {
  if (!isObject(data)) return { payload: {} };

  if (data.payload !== undefined) {
    return {
      ...data,
      payload: normalizeLaisContact(data.payload),
    };
  }

  return normalizeLaisContact(data);
};

const withNormalizedContacts = request =>
  request.then(response => ({
    ...response,
    data: normalizeContactsListPayload(response.data),
  }));

const withNormalizedContact = request =>
  request.then(response => ({
    ...response,
    data: normalizeContactPayload(response.data),
  }));

class LaisCrmContactsAPI extends ApiClient {
  constructor() {
    super('contacts', { accountScoped: true });
  }

  list(params = {}) {
    return withNormalizedContacts(axios.get(this.url, requestConfig(params)));
  }

  count(params = {}) {
    return this.list({
      include_contact_inboxes: 'false',
      page: '1',
      per_page: '1',
      ...params,
    }).then(response => ({
      count: contactsResponseCount(response.data),
      tagCount: Number(
        firstPresent(
          response.data?.meta?.new_count,
          response.data?.meta?.newCount,
          0
        )
      ),
      data: response.data,
    }));
  }

  search(params = {}) {
    return withNormalizedContacts(
      axios.get(resourcePath(this.url, 'search'), requestConfig(params))
    );
  }

  show(id) {
    return withNormalizedContact(
      axios.get(resourcePath(this.url, id), requestConfig())
    );
  }

  update(id, payload = {}) {
    return axios.patch(resourcePath(this.url, id), payload, requestConfig());
  }

  destroy(id) {
    return axios.delete(resourcePath(this.url, id), requestConfig());
  }

  exportLeads(payload = {}) {
    return axios.post(resourcePath(this.url, 'export_leads'), payload, {
      ...requestConfig(),
      responseType: 'blob',
    });
  }

  syncToCrm(contactIds = []) {
    return axios.post(
      resourcePath(this.url, 'sync_to_crm'),
      { contact_ids: contactIds },
      requestConfig()
    );
  }
}

export default new LaisCrmContactsAPI();
